import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { AppError, AUTH_ACTION_TYPE, MessageScopeType, MessageService, MessageType } from "@obg/ngx.core";
import { merge, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

import { BALANCE_LIMIT_ACTION_TYPE, BalanceLimitAction } from "./balance-limit.action";
import { BALANCE_LIMIT_MESSAGE_CODE, BALANCE_LIMIT_MESSAGE_CONTEXT } from "./balance-limit.const";
import { BalanceLimitService } from "./balance-limit.service";

@Injectable()
export class BalanceLimitEffect {
	balanceLimit$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BALANCE_LIMIT_ACTION_TYPE.get),
			switchMap(() =>
				this.service.get().pipe(
					map((response) => this.action.getSuccess(response)),
					catchError((error: AppError) => of(this.action.getFail(error)))
				)
			)
		)
	);

	balanceLimitUpdate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BALANCE_LIMIT_ACTION_TYPE.update),
			map((x: ActionWithPayload<any>) => x.payload),
			switchMap((params) =>
				this.service.update(params).pipe(
					map(() => this.action.updateSuccess()),
					catchError((error: AppError) => of(this.action.updateFail(error)))
				)
			)
		)
	);

	balanceLimitUpdateSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(BALANCE_LIMIT_ACTION_TYPE.updateSuccess),
			map(() => this.action.get())
		)
	);

	sessionEnd$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			map(() => this.action.reset())
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapClearByContext$({
				[BALANCE_LIMIT_ACTION_TYPE.get]: BALANCE_LIMIT_MESSAGE_CONTEXT.get,
				[BALANCE_LIMIT_ACTION_TYPE.update]: BALANCE_LIMIT_MESSAGE_CONTEXT.update,
			}),
			this.messageService.map$({
				[BALANCE_LIMIT_ACTION_TYPE.getSuccess]: {
					context: BALANCE_LIMIT_MESSAGE_CONTEXT.get,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: "",
				},
				[BALANCE_LIMIT_ACTION_TYPE.getFail]: {
					context: BALANCE_LIMIT_MESSAGE_CONTEXT.get,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: BALANCE_LIMIT_MESSAGE_CODE.getError,
				},
				[BALANCE_LIMIT_ACTION_TYPE.updateSuccess]: {
					context: BALANCE_LIMIT_MESSAGE_CONTEXT.update,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: BALANCE_LIMIT_MESSAGE_CODE.updateSuccess,
				},
				[BALANCE_LIMIT_ACTION_TYPE.updateFail]: {
					context: BALANCE_LIMIT_MESSAGE_CONTEXT.update,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: BALANCE_LIMIT_MESSAGE_CODE.updateError,
				},
			})
		)
	);

	constructor(
		private actions$: Actions,
		private action: BalanceLimitAction,
		private service: BalanceLimitService,
		private messageService: MessageService
	) {}
}
